// App.tsx
import React, { lazy, Suspense, useEffect } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { ROUTES } from './utils/constants';
import { ScrollToTop } from './components/ScrollToTop';
import { AdSenseScript } from './components/ads/AdSenseScript';
import { rememberLastOpenedGame } from './features/selector/last-game';
import { isLegacyComptourHostname } from './features/migration/domainMigration';

const Home = lazy(() => import('./pages/Home').then((module) => ({ default: module.Home })));
const Game = lazy(() => import('./pages/Game').then((module) => ({ default: module.Game })));
const Results = lazy(() => import('./pages/Results').then((module) => ({ default: module.Results })));
const LegalNotice = lazy(() => import('./pages/LegalNotice').then((module) => ({ default: module.LegalNotice })));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy').then((module) => ({ default: module.PrivacyPolicy })));
const GameSelector = lazy(() => import('./pages/GameSelector').then((module) => ({ default: module.GameSelector })));
const Flip7 = lazy(() => import('./pages/Flip7').then((module) => ({ default: module.Flip7 })));
const Skyjo = lazy(() => import('./pages/Skyjo').then((module) => ({ default: module.Skyjo })));
const LegacyDomainMigration = lazy(() => import('./features/migration/LegacyDomainMigration').then((module) => ({
  default: module.LegacyDomainMigration,
})));
const MascotMotionPreview = lazy(() => import('./features/mascots/MascotMotionPreview').then((module) => ({
  default: module.MascotMotionPreview,
})));

const App = () => {
  const location = useLocation();
  const isLegacyDomain = isLegacyComptourHostname(window.location.hostname);
  const canLoadAdSense = location.pathname !== ROUTES.LEGAL && location.pathname !== ROUTES.PRIVACY;

  useEffect(() => {
    const isSkullKingRoute = [ROUTES.HOME, ROUTES.GAME, ROUTES.RESULTS].includes(location.pathname);
    const isFlipSevenRoute = location.pathname.startsWith(ROUTES.FLIP7);
    const isSkyjoRoute = location.pathname.startsWith(ROUTES.SKYJO);
    const isSelectorRoute = location.pathname === ROUTES.SELECTOR;

    if (isSkullKingRoute) rememberLastOpenedGame('skullKing');
    if (isFlipSevenRoute) rememberLastOpenedGame('flip7');
    if (isSkyjoRoute) rememberLastOpenedGame('skyjo');
    document.title = isSkullKingRoute
      ? 'Skull King · Comptour'
      : isFlipSevenRoute
        ? 'Flip Seven · Comptour'
        : isSkyjoRoute
          ? 'SkyJo · Comptour'
          : 'Comptour · Le score de vos parties';

    document.documentElement.classList.toggle('route-flip7', isFlipSevenRoute);
    document.body.classList.toggle('route-flip7', isFlipSevenRoute);
    document.documentElement.classList.toggle('route-skyjo', isSkyjoRoute);
    document.body.classList.toggle('route-skyjo', isSkyjoRoute);
    document.documentElement.classList.toggle('route-skullking', isSkullKingRoute);
    document.body.classList.toggle('route-skullking', isSkullKingRoute);
    document.documentElement.classList.toggle('route-selector', isSelectorRoute && !isLegacyDomain);
    document.body.classList.toggle('route-selector', isSelectorRoute && !isLegacyDomain);
    document.documentElement.classList.toggle('route-migration', isLegacyDomain);
    document.body.classList.toggle('route-migration', isLegacyDomain);
    document.querySelector<HTMLMetaElement>('meta[name="theme-color"]')?.setAttribute(
      'content',
      isLegacyDomain ? '#f7f3eb' : isFlipSevenRoute ? '#07143e' : isSkyjoRoute ? '#111b46' : isSelectorRoute ? '#f7f3eb' : '#0D0B0A',
    );

    return () => {
      document.documentElement.classList.remove('route-flip7', 'route-skyjo', 'route-skullking', 'route-selector', 'route-migration');
      document.body.classList.remove('route-flip7', 'route-skyjo', 'route-skullking', 'route-selector', 'route-migration');
    };
  }, [isLegacyDomain, location.pathname]);

  if (isLegacyDomain) return (
    <Suspense fallback={null}>
      <LegacyDomainMigration />
    </Suspense>
  );

  return (
    <div className={`app-shell ${location.pathname === ROUTES.SELECTOR ? 'app-shell--selector' : location.pathname.startsWith(ROUTES.FLIP7) ? 'app-shell--flip7' : location.pathname.startsWith(ROUTES.SKYJO) ? 'app-shell--skyjo' : 'app-shell--default'}`}>
      <ScrollToTop />
      {canLoadAdSense && <AdSenseScript />}
      <AnimatePresence mode="wait">
        <Suspense fallback={null}>
          <Routes location={location} key={location.pathname}>
            <Route path={ROUTES.SELECTOR} element={<GameSelector />} />
            <Route path={ROUTES.HOME} element={<Home />} />
            <Route path={ROUTES.FLIP7} element={<Flip7 />} />
            <Route path={ROUTES.SKYJO} element={<Skyjo />} />
            <Route path={ROUTES.GAME} element={<Game />} />
            <Route path={ROUTES.RESULTS} element={<Results />} />
            <Route path={ROUTES.LEGAL} element={<LegalNotice />} />
            <Route path={ROUTES.PRIVACY} element={<PrivacyPolicy />} />
            {process.env.NODE_ENV !== 'production' ? <Route path="/__mascot-qa" element={<MascotMotionPreview />} /> : null}
          </Routes>
        </Suspense>
      </AnimatePresence>
    </div>
  );
};

export default App;
